import { NgTemplateOutlet } from '@angular/common';
import { Subscription } from 'rxjs';

import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostBinding, Input,
  OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild
} from '@angular/core';

import { BrandThemesEnum, ThemesEnum } from '../../../_enums/themes.enum';
import { ComponentWrapperWidthEnum } from '../../../_enums/component-wrapper.enum';
import { DeviceService } from '../../../_services/device.service';
import { IComponentWrapper } from '../../../_interfaces/component-wrapper.interface';
import { IExtendedLink, ILinkMedia } from '../../../_interfaces/link.interface';
import { MarginEnum } from '../../../_enums/margin.enum';
import { OrientationEnum } from '../../../_enums/orientation.enum';
import { PositionEnum } from '../../../_enums/position.enum';
import { SdBannerComponent } from '../../medias/banner/banner.component';
import { SdFootnoteComponent } from '../../footnote/footnote.component';
import { SdHeadingComponent } from '../../heading/heading.component';
import { SdLibConfig } from '../../../sd-lib-config';
import { SdLinkComponent } from '../../links/link/link.component';
import { SdThemeComponent } from '../../abstract/theme/theme.component';
import { ThemesCssVariablesMapper } from '@seb/web-design-system/core';

@Component({
  selector: 'sd-component-wrapper',
  templateUrl: './component-wrapper.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdBannerComponent,
    SdHeadingComponent,
    NgTemplateOutlet,
    SdFootnoteComponent,
    SdLinkComponent
  ]
})
export class SdComponentWrapperComponent extends SdThemeComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input()
  isLoading = false;

  @Input()
  componentWidth: ComponentWrapperWidthEnum = ComponentWrapperWidthEnum.default;

  @Input()
  titleLevel: number;

  @Input()
  content: IComponentWrapper;

  @Input()
  marginSize: MarginEnum.default_margin | MarginEnum.without_margin;

  @Input()
  layout: OrientationEnum.row | OrientationEnum.column = OrientationEnum.column;

  @Input()
  textConfig: PositionEnum.center | PositionEnum.left | PositionEnum.right = PositionEnum.center;

  /**
   * Allow to define if the text is aligned on the left or on right, or even centered (only in column mod / row mobile)
   * * @param {string} left | right | center
   */
  @Input()
  layoutTextConfig: PositionEnum.left | PositionEnum.right | PositionEnum.center = PositionEnum.center;

  @Input()
  themesConfig: ThemesEnum | BrandThemesEnum;

  @Output()
  linkClicked = new EventEmitter<any>();

  @ViewChild('componentWrapper')
  componentWrapper;

  @Input() class = '';
  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.class,
      this.themesConfig ? 'is-theme-' + this.themesConfig : '',
      this.content?.background ? 'has-background' : '',
      this.layout
        ? this.isDevice$
          ? 'is-column'
          : 'is-' + this.layout
        : 'is-column',
      this.componentMargin ? 'has-margin-' + this.componentMargin : '',
      this.layoutTextConfig ? 'is-layout-' + this.layoutTextConfig : '',
      this.textConfig ? 'is-' + this.textConfig + '-aligned' : '',
      this.componentWidth ? this.componentWidth : ''
    ]
      .filter(Boolean)
      .join(' ');
  }

  componentMargin: MarginEnum;
  isDevice$: boolean;
  deviceSubscription: Subscription;
  orientationEnum = OrientationEnum;
  positionEnum = PositionEnum;
  emptyComponent = false;
  hasNoData = false;
  brandThemesEnum = BrandThemesEnum;

  constructor(
    private deviceService: DeviceService,
    private sdLibConfig: SdLibConfig,
    private cdRef: ChangeDetectorRef
  ) {
    super();

    this.componentMargin = this.sdLibConfig.get().componentMargin;
  }

  ngOnInit() {
    this.deviceSubscription = this.deviceService.isDevice.subscribe((x) => {
      this.isDevice$ = x;
      this.cdRef.markForCheck();
    });
  }

  ngOnChanges(changes?: SimpleChanges) {
    super.ngOnChanges(changes);
    this.componentMargin = this.marginSize ?? this.sdLibConfig.get(this.brand).componentMargin;
  }

  ngAfterViewInit() {
    this.emptyComponent = !this.componentWrapper?.nativeElement?.childNodes?.length;

    setTimeout(() => {
      const themeCssVar = this.themesConfig ? ThemesCssVariablesMapper.mapBrandTheme(this.themesConfig) : '';
      this.updateMainBackground(themeCssVar);
    });
  }

  ngOnDestroy() {
    this.deviceSubscription?.unsubscribe();
  }

  checkData(event: boolean) {
    this.hasNoData = event;
  }

  onClickLink(event: IExtendedLink | ILinkMedia) {
    this.linkClicked.emit({ link: event, isBrandCorner: !!this.brand });
  }
}
